import React from "react";
import "./FrontPage.css";

// ── The Sunday Edition (Weekly Review), rendered in the Page One aesthetic. ────
//
// Content comes from /api/sunday-editions/latest (App passes it as `edition`).
// Two hard rules govern what we print:
//   1. Method concealment (STANDING RULE): the DIME × PMESII-PTT cross-impact
//      matrix is computed internally but must NEVER surface in a customer product.
//      We whitelist known-safe prose modules and additionally hard-block any key
//      whose name hints at the concealed method — belt and suspenders.
//   2. Defensive rendering: the populated edition shape can vary, so we only emit
//      string / string[] values under recognized titles and skip everything else.

const str = (v) => (v && typeof v === "object" && "text" in v ? v.text : v == null ? "" : String(v));
const toParas = (v) => {
  if (Array.isArray(v)) return v.map(str).map((s) => s.trim()).filter(Boolean);
  const t = str(v).trim();
  if (!t) return [];
  const byBlank = t.split(/\n\s*\n/).map((s) => s.trim()).filter(Boolean);
  return byBlank.length > 1 ? byBlank : [t];
};

// Recognized, customer-safe modules → display title. Keys may arrive bare or with
// a `weekly_review_` prefix (the DB column names); we normalize before matching.
const SAFE_TITLES = {
  bluf: "The Week in Brief",
  tldr: "The Week in Brief",
  summary: "The Week in Brief",
  cross_domain: "Cross-Domain Assessment",
  domain_cascade: "Domain Cascade",
  supply_chain: "Supply Chain",
  counter_tech: "Counter-Technology",
  destabilizing_effectors: "Destabilizing Effectors",
  course_of_action: "Outlook & Courses of Action",
  integrity_annex: "Sourcing & Integrity",
};
// Never print anything naming the concealed analytic method.
const CONCEAL = /dime|pmesii|cross.?impact|matrix/i;

const ORDER = ["bluf", "tldr", "summary", "cross_domain", "domain_cascade", "supply_chain",
  "destabilizing_effectors", "counter_tech", "course_of_action", "integrity_annex"];

function safeSections(edition) {
  if (!edition || typeof edition !== "object") return [];
  const norm = {};
  for (const [k, v] of Object.entries(edition)) {
    const key = k.replace(/^weekly_review_/, "");
    if (CONCEAL.test(k)) continue;              // hard concealment block
    if (!(key in SAFE_TITLES)) continue;        // whitelist only
    const paras = toParas(v);
    if (paras.length) norm[key] = paras;
  }
  const seen = new Set();
  const out = [];
  for (const key of ORDER) {
    if (norm[key] && !seen.has(SAFE_TITLES[key])) { out.push([SAFE_TITLES[key], norm[key]]); seen.add(SAFE_TITLES[key]); }
  }
  return out;
}

export default function SundayEdition({ edition, dateStr }) {
  const sections = safeSections(edition);
  return (
    <div className="bi-p1 p1-subpage">
      <div className="p1-topline">
        <span>The Sunday Edition</span>
        <span className="p1-sep">|</span>
        <span>Weekly Review</span>
        {dateStr && <><span className="p1-sep">|</span><span>{dateStr}</span></>}
      </div>
      <div className="p1-banner"><span className="p1-rule" /><span className="p1-banner-t">The Weekly Review</span><span className="p1-rule" /></div>
      <div className="p1-hr-thick" />

      {sections.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px 16px 24px" }}>
          <div className="p1-kicker p1-center">Special Sunday Supplement</div>
          <h1 className="p1-lead-hl" style={{ marginTop: 8 }}>The Weekly Review Is Being Composed</h1>
          <p className="p1-deck">This week's edition — the seven-day pattern read across every domain — publishes with the Sunday brief. Check back once today's edition goes to press.</p>
        </div>
      ) : (
        sections.map(([title, paras], i) => (
          <section key={i} style={{ margin: "8px 0 4px" }}>
            {i > 0 && <div className="p1-hr" />}
            <h2 className="p1-hl" style={{ textAlign: "left", fontSize: 22 }}>{title}</h2>
            <div className="p1-lead-body p1-article-body">
              {paras.map((p, j) => <p key={j} className={j === 0 ? "p1-drop" : ""}>{p}</p>)}
            </div>
          </section>
        ))
      )}
    </div>
  );
}
